import { useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import type { JobOverview, JobRun, Prescription, SourceRun } from '@joker/contracts'
import { useTRPC, queryClient } from '../trpc'
import { Panel, Tile, Loading, ErrorNote, fmtDate, ViewTitle } from '../ui'

/**
 * The worker, made visible: one row per job with its last run, and the
 * prescription job's own table underneath, since that is the one whose output
 * somebody reads. docs/engineering/prescription-eligibility.md
 */

const DOT: Record<string, string> = { succeeded: 'ok', running: 'warn', failed: 'bad' }

export const GRADE_LABEL: Record<string, string> = {
  suitable: 'Suitable',
  not_suitable: 'Not suitable',
  refer: 'Refer',
  needs_answers: 'Ask',
  incomplete: 'Incomplete',
}
export const GRADE_DOT: Record<string, string> = {
  suitable: 'ok',
  not_suitable: 'bad',
  refer: 'warn',
  needs_answers: 'warn',
  incomplete: 'bad',
}

const KIND_LABEL: Record<string, string> = {
  receipt: 'receipt',
  invoice: 'invoice',
  order_confirmation: 'order confirmation',
  shipping_label: 'shipping label',
  eye_exam_report: 'eye exam report',
  photo: 'photo',
  screenshot: 'screenshot',
  letter: 'letter',
  other: 'other',
}

const VERDICT_LABEL: Record<string, string> = {
  prescription: 'Prescription',
  not_prescription: 'Not a prescription',
  unreadable: 'Unreadable',
  failed: 'Failed',
}

const duration = (r: JobRun) => {
  const end = r.finishedAt ? new Date(r.finishedAt).getTime() : Date.now()
  const s = Math.round((end - new Date(r.startedAt).getTime()) / 1000)
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`
}

export const fmtRx = (sph: number | null, cyl: number | null, axis: number | null): string => {
  if (sph == null && cyl == null) return '—'
  const f = (n: number) => `${n > 0 ? '+' : n < 0 ? '−' : ''}${Math.abs(n).toFixed(2)}`
  let s = sph == null ? '?' : f(sph)
  if (cyl != null) s += ` / ${f(cyl)}`
  if (axis != null) s += ` × ${axis}`
  return s
}

/** Live while anything runs: every 3 s, then back to on-demand. */
const LIVE_MS = 3000

export function Jobs({ onOpenPerson }: { onOpenPerson: (id: string) => void }) {
  const trpc = useTRPC()
  const overview = useQuery({
    ...trpc.jobs.overview.queryOptions(),
    refetchInterval: (q) => (q.state.data?.some((j) => j.lastRun?.status === 'running') ? LIVE_MS : false),
  })
  const sources = useQuery({
    ...trpc.jobs.sources.queryOptions(),
    refetchInterval: (q) => (q.state.data?.some((s) => s.status === 'running') ? LIVE_MS : false),
  })
  const anythingRunning =
    (overview.data?.some((j) => j.lastRun?.status === 'running') ?? false) ||
    (sources.data?.some((s) => s.status === 'running') ?? false)
  const totals = useQuery({
    ...trpc.prescriptions.totals.queryOptions(),
    refetchInterval: anythingRunning ? LIVE_MS : false,
  })
  const [verdict, setVerdict] = useState<Prescription['verdict'] | ''>('')
  const [grade, setGrade] = useState<string>('')
  const [unmatched, setUnmatched] = useState(false)
  const list = useQuery({
    ...trpc.prescriptions.list.queryOptions({
      ...(verdict ? { verdict } : {}),
      ...(grade ? { grade: grade as Prescription['grade'] & string } : {}),
      ...(unmatched ? { unmatched: true } : {}),
      limit: 200,
    }),
    refetchInterval: anythingRunning ? LIVE_MS : false,
  })
  const run = useMutation(trpc.jobs.run.mutationOptions({ onSuccess: () => queryClient.invalidateQueries() }))
  const regrade = useMutation(
    trpc.jobs.regradePrescriptions.mutationOptions({ onSuccess: () => queryClient.invalidateQueries() }),
  )
  const retry = useMutation(
    trpc.jobs.retryFailedPrescriptions.mutationOptions({ onSuccess: () => queryClient.invalidateQueries() }),
  )
  const classify = useMutation(
    trpc.jobs.classifyPrescriptions.mutationOptions({ onSuccess: () => queryClient.invalidateQueries() }),
  )
  const [historyFor, setHistoryFor] = useState<string | null>(null)

  if (overview.error) return <ErrorNote error={overview.error} />
  if (!overview.data) return <Loading what="jobs" />

  return (
    <>
      <ViewTitle suit="joker-red">Jobs</ViewTitle>
      <p className="view-sub">
        What the worker did last, and what the prescription job read out of the mail. A run that
        stops checking in for ten minutes is closed as abandoned and the next one takes over.
      </p>

      {totals.data && (
        <div className="tiles">
          <Tile k="Files scanned" v={totals.data.scanned} d="mail attachments and uploads" />
          <Tile k="Prescriptions found" v={totals.data.prescriptions} d="distinct files with a sphere read" />
          <Tile k="Unmatched" v={totals.data.unmatched} d="no person found; attach by hand" />
          {totals.data.failed > 0 && <Tile k="Failed" v={totals.data.failed} d="out of attempts; retry below" />}
          {totals.data.byGrade.map((g) => (
            <Tile key={g.grade} k={GRADE_LABEL[g.grade] ?? g.grade} v={g.count} d="distinct prescriptions" />
          ))}
          <Tile
            k="Mails about an Rx with no file"
            v={totals.data.mailsMentioningRxWithoutFile}
            d="typed into the mail; not read yet"
          />
        </div>
      )}

      <Panel title="Importer" note="what each source last pulled; the mail pull feeds the prescription job">
        {!sources.data ? (
          <Loading what="source runs" />
        ) : (
          <div className="scroll-x">
            <table className="data" style={{ minWidth: 640 }}>
              <thead>
                <tr>
                  <th>Source</th>
                  <th>Last run</th>
                  <th className="r">Fetched</th>
                  <th className="r">New</th>
                </tr>
              </thead>
              <tbody>
                {sources.data.map((s: SourceRun) => (
                  <tr key={s.source} style={{ cursor: 'default' }}>
                    <td>
                      <span className={`dot ${DOT[s.status] ?? 'warn'}`} style={{ display: 'inline-block', marginRight: 8 }} />
                      <strong>{s.label}</strong>
                      {s.error && (
                        <>
                          <br />
                          <span className="bad-text">{s.error.slice(0, 160)}</span>
                        </>
                      )}
                    </td>
                    <td className="muted">
                      {fmtDate(s.startedAt)} · {s.status} · {s.mode}
                      {s.status === 'running' && <span className="warn-text"> · pulling… {s.rowsFetched.toLocaleString()} so far</span>}
                    </td>
                    <td className="r num">{s.rowsFetched.toLocaleString()}</td>
                    <td className="r num">{s.rowsLanded.toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <Panel title="Worker jobs" note="when each last ran, and everything it has ever done">
        <div className="scroll-x">
          <table className="data" style={{ minWidth: 860 }}>
            <thead>
              <tr>
                <th>Job</th>
                <th>Last run</th>
                <th className="r">Runs</th>
                <th className="r">Scanned</th>
                <th className="r">Found</th>
                <th className="r">Skipped</th>
                <th className="r">Failed</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {overview.data.map((j: JobOverview) => (
                <tr key={j.job} style={{ cursor: 'default' }}>
                  <td>
                    <span
                      className={`dot ${j.lastRun ? DOT[j.lastRun.status] : 'bad'}`}
                      style={{ display: 'inline-block', marginRight: 8 }}
                    />
                    <strong>{j.label}</strong>
                    {j.configured === false && (
                      <>
                        <br />
                        <span className="muted" style={{ fontSize: 11.5 }}>
                          Not configured — BEDROCK_REGION and BEDROCK_MODEL_ID
                        </span>
                      </>
                    )}
                    {j.lastRun?.error && (
                      <>
                        <br />
                        <span className="bad-text">{j.lastRun.error.slice(0, 160)}</span>
                      </>
                    )}
                  </td>
                  <td className="muted">
                    {j.lastRun ? `${fmtDate(j.lastRun.startedAt)} ${timeOf(j.lastRun.startedAt)} · ${j.lastRun.status}` : 'never'}
                    {j.lastRun?.status === 'running' && <Progress run={j.lastRun} />}
                  </td>
                  <td className="r num">{j.totals.runs}</td>
                  <td className="r num">{j.job === 'extract.prescriptions' ? j.totals.scanned : '—'}</td>
                  <td className="r num">{j.totals.succeeded}</td>
                  <td className="r num">{j.job === 'extract.prescriptions' ? j.totals.skipped : '—'}</td>
                  <td className="r num">{j.totals.failed}</td>
                  <td className="r" style={{ whiteSpace: 'nowrap' }}>
                    {(j.job === 'extract.prescriptions' || j.job === 'compute.metrics') && (
                      <button
                        type="button"
                        className="btn sm"
                        disabled={!j.canRun || run.isPending}
                        title={j.cooldownSeconds > 0 ? `Ran recently; ${Math.ceil(j.cooldownSeconds / 60)}m to wait` : undefined}
                        onClick={() => run.mutate({ job: j.job as 'extract.prescriptions' | 'compute.metrics' })}
                      >
                        {j.lastRun?.status === 'running' ? 'Running…' : run.isPending ? 'Starting…' : 'Run now'}
                      </button>
                    )}{' '}
                    {j.lastRun && (
                      <button type="button" className="btn sm ghost" onClick={() => setHistoryFor(j.job)}>
                        History
                      </button>
                    )}
                    {j.cooldownSeconds > 0 && j.lastRun?.status !== 'running' && (
                      <>
                        <br />
                        <span className="muted" style={{ fontSize: 11.5 }}>
                          again in {Math.ceil(j.cooldownSeconds / 60)}m
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {run.error && <p className="bad-text">{run.error.message}</p>}
        <p className="footnote">
          Scanned, found, skipped and failed add up over every run. For prescriptions, found means a
          prescription with values read; skipped is a file that was not one, or could not be read.
          Prescriptions run hourly at :25, after the mail sync at :12, and straight after any sync
          or upload that brings a new file.{' '}
          <button className="chip" style={{ cursor: 'pointer' }} disabled={regrade.isPending} onClick={() => regrade.mutate()}>
            {regrade.isPending ? 'Queued…' : 'Re-grade every prescription with the current rule'}
          </button>{' '}
          <button className="chip" style={{ cursor: 'pointer' }} disabled={classify.isPending} onClick={() => classify.mutate()}>
            {classify.isPending ? 'Queued…' : 'Name the kind of every file that is not a prescription'}
          </button>{' '}
          {(totals.data?.failed ?? 0) > 0 && (
            <button className="chip" style={{ cursor: 'pointer' }} disabled={retry.isPending} onClick={() => retry.mutate()}>
              {retry.isPending ? 'Queued…' : `Retry ${totals.data!.failed} failed file${totals.data!.failed === 1 ? '' : 's'}`}
            </button>
          )}
        </p>
        {historyFor && (
          <RunHistoryWindow
            job={historyFor}
            label={overview.data.find((j) => j.job === historyFor)?.label ?? historyFor}
            onClose={() => setHistoryFor(null)}
          />
        )}
      </Panel>

      <Panel title="Scanned files" note="newest first">
        <div className="pform-actions" style={{ marginBottom: 10 }}>
          <select value={verdict} onChange={(e) => setVerdict(e.target.value as Prescription['verdict'] | '')}>
            <option value="">Any verdict</option>
            {Object.entries(VERDICT_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <select value={grade} onChange={(e) => setGrade(e.target.value)}>
            <option value="">Any grade</option>
            {Object.entries(GRADE_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </select>
          <label>
            <input type="checkbox" checked={unmatched} onChange={(e) => setUnmatched(e.target.checked)} /> Unmatched only
          </label>
        </div>
        {list.error ? (
          <ErrorNote error={list.error} />
        ) : !list.data ? (
          <Loading what="scanned files" />
        ) : list.data.length === 0 ? (
          <p className="footnote">Nothing scanned yet.</p>
        ) : (
          <div className="scroll-x">
            <table className="data" style={{ minWidth: 900 }}>
              <thead>
                <tr>
                  <th>Date</th>
                  <th>From</th>
                  <th>File</th>
                  <th>Verdict</th>
                  <th>Right</th>
                  <th>Left</th>
                  <th className="r">Add</th>
                  <th>Grade</th>
                  <th>Person</th>
                  <th className="r">Tokens</th>
                </tr>
              </thead>
              <tbody>
                {list.data.map((p: Prescription) => (
                  <tr key={p.id} style={{ cursor: 'default' }}>
                    <td className="num">{fmtDate(p.documentDate)}</td>
                    <td className="muted">{p.source === 'upload' ? 'upload' : (p.mailbox ?? '').split('@')[0]}</td>
                    <td>
                      <a href={`/api/prescriptions/${p.id}/file`} target="_blank" rel="noopener">
                        {p.filename ?? p.mimeType}
                      </a>
                    </td>
                    <td>
                      {VERDICT_LABEL[p.verdict]}
                      {p.documentKind && p.documentKind !== 'prescription' && (
                        <span className="muted"> · {KIND_LABEL[p.documentKind] ?? p.documentKind}</span>
                      )}
                      {p.error && (
                        <>
                          <br />
                          <span className="bad-text">{p.error.slice(0, 120)}</span>
                        </>
                      )}
                    </td>
                    <td className="num">{fmtRx(p.od.sph, p.od.cyl, p.od.axis)}</td>
                    <td className="num">{fmtRx(p.os.sph, p.os.cyl, p.os.axis)}</td>
                    <td className="r num">{p.add == null ? '—' : `+${p.add.toFixed(2)}`}</td>
                    <td>
                      <GradeChip grade={p.gradeOverride ?? p.grade} override={p.gradeOverride != null} />
                    </td>
                    <td>
                      {p.personId ? (
                        <button type="button" className="btn sm ghost" onClick={() => onOpenPerson(p.personId!)}>
                          {p.personName ?? 'Open'}
                        </button>
                      ) : p.verdict === 'prescription' ? (
                        <span className="warn-text">unmatched{p.patientName ? ` · ${p.patientName}` : ''}</span>
                      ) : (
                        <span className="muted">—</span>
                      )}
                    </td>
                    <td className="r num muted">
                      {p.inputTokens == null ? '—' : `${p.inputTokens.toLocaleString()} / ${p.outputTokens ?? 0}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="footnote">
          The file opens in its own tab. Values shown are what the model read; the grade is the
          sales guide applied to them, and a hand-set grade is marked. Unmatched prescriptions name
          nobody Joker knows yet: attach them from the lead’s page once the person exists.
        </p>
      </Panel>
    </>
  )
}

export function GradeChip({ grade, override }: { grade: string | null; override?: boolean }) {
  if (!grade) return <span className="muted">—</span>
  return (
    <span>
      <span className={`dot ${GRADE_DOT[grade] ?? 'warn'}`} style={{ display: 'inline-block', marginRight: 6 }} />
      {GRADE_LABEL[grade] ?? grade}
      {override && <span className="muted"> (set by hand)</span>}
    </span>
  )
}

/** A thin bar under a running job: how far through the work it found. */
function Progress({ run }: { run: JobRun }) {
  const pct = run.total ? Math.min(100, Math.round((run.scanned / run.total) * 100)) : null
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ height: 6, background: 'var(--surface-2)', borderRadius: 3, overflow: 'hidden', maxWidth: 220 }}>
        <div
          style={{
            height: '100%',
            width: pct == null ? '30%' : `${pct}%`,
            background: 'var(--warning)',
            transition: 'width .4s',
          }}
        />
      </div>
      <span className="warn-text" style={{ fontSize: 11.5 }}>
        {pct == null ? `${run.scanned} done · working…` : `${run.scanned} of ${run.total} · ${pct}%`}
        {run.note ? ` · ${run.note}` : ''}
      </span>
    </div>
  )
}

const timeOf = (iso: string) => {
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`
}

/** The last fifty runs of one job, newest first, in a window over the page. */
function RunHistoryWindow({ job, label, onClose }: { job: string; label: string; onClose: () => void }) {
  const trpc = useTRPC()
  const q = useQuery(trpc.jobs.runs.queryOptions({ job, limit: 50 }))
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.45)', zIndex: 50, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: 'var(--surface)', color: 'inherit', borderRadius: 10, maxWidth: 1000, width: '100%', maxHeight: '85vh', overflow: 'auto', padding: 16, boxShadow: '0 12px 40px rgba(0,0,0,.35)' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 10 }}>
          <strong style={{ fontSize: 15 }}>{label} · run history</strong>
          <span className="spacer" style={{ flex: 1 }} />
          <button type="button" className="btn sm" onClick={onClose}>
            Close
          </button>
        </div>
        {q.error ? <ErrorNote error={q.error} /> : !q.data ? <Loading what="runs" /> : <RunTable runs={q.data} />}
      </div>
    </div>
  )
}

function RunTable({ runs }: { runs: JobRun[] }) {
  return (
    <table className="data" style={{ fontSize: 12 }}>
      <thead>
        <tr>
          <th>Started</th>
          <th>Status</th>
          <th>Mode</th>
          <th>Took</th>
          <th className="r">Scanned</th>
          <th className="r">Done</th>
          <th className="r">Skipped</th>
          <th className="r">Failed</th>
          <th>Note</th>
        </tr>
      </thead>
      <tbody>
        {runs.map((r) => (
          <tr key={r.id} style={{ cursor: 'default' }}>
            <td className="num">{fmtDate(r.startedAt)} {timeOf(r.startedAt)}</td>
            <td>{r.status}</td>
            <td className="muted">{r.mode}</td>
            <td className="muted">{duration(r)}</td>
            <td className="r num">{r.total != null ? `${r.scanned} of ${r.total}` : r.scanned}</td>
            <td className="r num">{r.succeeded}</td>
            <td className="r num">{r.skipped}</td>
            <td className="r num">{r.failed}</td>
            <td className="muted">{r.error ? <span className="bad-text">{r.error.slice(0, 100)}</span> : (r.note ?? '')}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}
